import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axiosInstance from '../../api/axiosConfig';
import './BranchMappingTab.css';

/**
 * BranchMappingTab — pairs each SALES organization with the OWNERSHIP tenant it
 * belongs to.
 *
 * Background: salesmen hang off the Organization tree while a logged-in user
 * belongs to a Tenant branch, and until these are paired the backend cannot tell
 * which salesmen belong to the user's branch — so every branch saw every
 * salesman. `Organization.tenant` is that pair; this screen is where it is set.
 *
 * Two columns, deliberately distinct:
 *   Code   — the canonical identity ("BQF-KEY-SALES"), typed by humans, used by
 *            spreadsheet imports. Equal codes on both sides is what makes a pair
 *            obvious, but it is NOT the join.
 *   Linked — the actual join, stored as an id so it survives a rename.
 *
 * The banner is the point of the screen: an unlinked sales branch means its
 * salesmen are invisible to non-admins once scoping is enforced, so that number
 * has to be zero BEFORE SALESMAN_SCOPE is switched on.
 */
const BranchMappingTab = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [savingId, setSavingId] = useState(null);
    const [notice, setNotice] = useState(null);
    const [drafts, setDrafts] = useState({});

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await axiosInstance.get('/api/sales-target/organizations/tenant-mapping');
            setData(res.data.data);
            setDrafts({});
        } catch (err) {
            setError(err.response?.data?.message || 'Could not load the branch mapping.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    // Tenants are offered by level, because the backend rejects a mismatch: a
    // sales BRANCH pairs with a tenant BRANCH, a company or root with a tenant
    // COMPANY (which is how a head-office user resolves to a whole company).
    const tenantsByLevel = useMemo(() => {
        const tenants = data?.tenants || [];
        return {
            branch: tenants.filter((t) => t.type === 'branch'),
            company: tenants.filter((t) => t.type === 'company'),
        };
    }, [data]);

    const companyNameById = useMemo(() => {
        const map = new Map();
        (data?.tenants || []).forEach((t) => { if (t.type === 'company') map.set(String(t._id), t.name); });
        return map;
    }, [data]);

    const draftFor = (row, field) => {
        const draft = drafts[String(row._id)];
        if (draft && field in draft) return draft[field];
        return field === 'code' ? (row.code || '') : (row.tenant?._id ? String(row.tenant._id) : '');
    };

    const setDraft = (rowId, field, value) => {
        setDrafts((prev) => ({ ...prev, [rowId]: { ...prev[rowId], [field]: value } }));
    };

    const isDirty = (row) => {
        const draft = drafts[String(row._id)];
        if (!draft) return false;
        const codeChanged = 'code' in draft && draft.code !== (row.code || '');
        const tenantChanged = 'tenant' in draft
            && draft.tenant !== (row.tenant?._id ? String(row.tenant._id) : '');
        return codeChanged || tenantChanged;
    };

    const save = async (row) => {
        const rowId = String(row._id);
        setSavingId(rowId);
        setNotice(null);
        try {
            await axiosInstance.put(`/api/sales-target/organizations/${rowId}`, {
                code: draftFor(row, 'code'),
                // '' clears the link; the backend reads that as an explicit null.
                tenant: draftFor(row, 'tenant') || null,
            });
            setNotice({ type: 'ok', text: `Saved ${row.name}.` });
            await load();
        } catch (err) {
            setNotice({ type: 'err', text: err.response?.data?.message || `Could not save ${row.name}.` });
        } finally {
            setSavingId(null);
        }
    };

    if (loading) return <div className="bmap-state">Loading branch mapping…</div>;
    if (error) return <div className="bmap-state bmap-state-error">{error}</div>;
    if (!data) return null;

    const { organizations, summary } = data;
    const branches = organizations.filter((o) => o.type === 'branch');
    const higher = organizations.filter((o) => o.type !== 'branch');

    const renderRow = (row) => {
        const rowId = String(row._id);
        const options = tenantsByLevel[row.expects] || [];
        const codeValue = draftFor(row, 'code');
        const tenantValue = draftFor(row, 'tenant');
        const linkedCode = row.tenant?.code || '';
        // A pairing where both sides already agree on the code is the normal,
        // healthy case — worth showing so an odd pairing stands out.
        const codesAgree = linkedCode && codeValue && linkedCode === codeValue;

        return (
            <tr key={rowId} className={!row.tenant && row.type === 'branch' ? 'bmap-row-unlinked' : ''}>
                <td>
                    <div className="bmap-name">{row.name}</div>
                    <div className="bmap-sub">
                        {row.type}
                        {row.type === 'branch' && ` · ${row.activeSalesmen} salesman${row.activeSalesmen === 1 ? '' : 'men'}`}
                        {!row.active && ' · inactive'}
                    </div>
                </td>
                <td>
                    <input
                        className="bmap-input"
                        value={codeValue}
                        placeholder="e.g. BQF-KEY-SALES"
                        onChange={(e) => setDraft(rowId, 'code', e.target.value)}
                    />
                </td>
                <td>
                    <select
                        className="bmap-select"
                        value={tenantValue}
                        onChange={(e) => setDraft(rowId, 'tenant', e.target.value)}
                    >
                        <option value="">— not linked —</option>
                        {options.map((t) => (
                            <option key={t._id} value={String(t._id)}>
                                {t.code ? `${t.code} — ` : ''}{t.name}
                                {t.type === 'branch' && t.parent ? ` (${companyNameById.get(String(t.parent)) || ''})` : ''}
                                {t.branchType === 'head-office' ? ' · head office' : ''}
                            </option>
                        ))}
                    </select>
                    {codesAgree && <span className="bmap-badge-ok" title="Both sides carry the same code">codes match</span>}
                </td>
                <td className="bmap-actions">
                    <button
                        className="bmap-save"
                        disabled={!isDirty(row) || savingId === rowId}
                        onClick={() => save(row)}
                    >
                        {savingId === rowId ? 'Saving…' : 'Save'}
                    </button>
                </td>
            </tr>
        );
    };

    return (
        <div className="bmap">
            <div className={`bmap-banner ${summary.unlinkedBranches ? 'bmap-banner-warn' : 'bmap-banner-ok'}`}>
                <div>
                    <strong>
                        {summary.unlinkedBranches
                            ? `${summary.unlinkedBranches} of ${summary.totalBranches} sales branches are not linked`
                            : `All ${summary.totalBranches} sales branches are linked`}
                    </strong>
                    <div className="bmap-banner-sub">
                        {summary.unlinkedBranches
                            ? `${summary.salesmenAtRisk} active salesman${summary.salesmenAtRisk === 1 ? '' : 'men'} would be invisible to non-admins once salesman scoping is enforced.`
                            : 'Every salesman resolves to a branch. Scoping can be enforced safely.'}
                    </div>
                </div>
                <div className="bmap-mode">
                    scoping: <span className={summary.mode === 'enforced' ? 'bmap-mode-on' : 'bmap-mode-off'}>{summary.mode}</span>
                </div>
            </div>

            {notice && (
                <div className={`bmap-notice ${notice.type === 'ok' ? 'bmap-notice-ok' : 'bmap-notice-err'}`}>
                    {notice.text}
                </div>
            )}

            <p className="bmap-help">
                Each sales branch below must point at the company/branch its users log in under.
                The <strong>code</strong> is the shared name both systems use (imports match on it);
                the <strong>link</strong> is what actually decides which salesmen a user sees.
            </p>

            <table className="bmap-table">
                <thead>
                    <tr>
                        <th>Sales organization</th>
                        <th>Code</th>
                        <th>Linked company / branch</th>
                        <th />
                    </tr>
                </thead>
                <tbody>
                    <tr className="bmap-group"><td colSpan={4}>Branches</td></tr>
                    {branches.map(renderRow)}
                    <tr className="bmap-group">
                        <td colSpan={4}>
                            Companies &amp; group
                            <span className="bmap-group-note">
                                — a head-office user sees every branch beneath the node their company is linked to
                            </span>
                        </td>
                    </tr>
                    {higher.map(renderRow)}
                </tbody>
            </table>
        </div>
    );
};

export default BranchMappingTab;
